import {
  setActiveSidebarTab,
  showCreateBlockDialog,
} from 'client/actions/actionsManagement'
import * as entityActions from 'client/actions/entityActions'
import { OptionalRefInterface } from 'client/components/entities/Row/components/ColumnAlignControls'
import { color } from 'client/constants/color'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { useHighlightedEntityId } from 'client/context/HighlightedContext'
import { useIsRowResizing } from 'client/context/ResizingRowContext'
import { useSidebarVisibilityActionsContext } from 'client/context/SidebarVisibilityContext'
import { useAppDispatch } from 'client/hooks/use-dispatch'
import useManagementHook from 'client/hooks/useManagement'
import { isCreateBlockDialogActive } from 'client/reducers/managementReducer'
import { pageSelectors, useManagement, usePage } from 'client/store/index'
import { setEditingEntity } from 'client/store/management/managementActions'
import managementSelectors from 'client/store/management/managementSelectors'
import { getBubblingPath } from 'client/utils/get-bubbling-path'
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { PageTypeEnum } from 'common/enums/PageTypeEnum'
import { useActions } from 'common/hooks/useActions'
import EntityInterface from 'common/types/entities/EntityInterface'
import React, { ReactNode, useRef, useState } from 'react'
import { useDrag, useDragLayer } from 'react-dnd'
import { useIsAllowedWebsiteHeaderEntity } from 'src/editor/hooks/use-is-allowed-website-header-entity'
import { BaseEntityInterface } from 'src/shared/types/base-entity-interface'
import {
  NO_FOLLOW_CHECKBOX_ID,
  OPEN_IN_NEW_TAB_CHECKBOX_ID,
} from '../tiptap-editor/components/link-toolbar/link-toolbar'
import ActionsBox from './ActionsBox'
import DeviceWarning from './DeviceWarning'
import ReadOnlyEntity from './ReadOnlyEntity'
import BaseEntityNewUi from './ui/BaseEntityNewUi'
import * as utils from './utils'

export type BaseEntityProps = {
  entity: EntityInterface | BaseEntityInterface
  highlightedColor?: string
  hoverColor?: string
  isMoveDownAllowed: boolean
  isMoveUpAllowed: boolean
  isSaveAllowed?: boolean
  isRemoveAllowed?: boolean
  isCopyAllowed?: boolean
  isMoveAllowed?: boolean
  children?: ReactNode
  dropBoxAllowedEntityIds?: string[]
  flex?: boolean
  flexColumn?: boolean
  externalActionsRef?: React.RefObject<OptionalRefInterface>
  appearance?: {
    mobile: boolean
    desktop: boolean
  }
}

function BaseEntityNew({
  entity,
  highlightedColor = color.blue,
  hoverColor = color.orange,
  isMoveDownAllowed,
  isMoveUpAllowed,
  isSaveAllowed = true,
  isRemoveAllowed = true,
  isCopyAllowed = true,
  isMoveAllowed = true,
  dropBoxAllowedEntityIds,
  children,
  flex,
  flexColumn,
  externalActionsRef,
  appearance,
}: BaseEntityProps) {
  const dispatch = useAppDispatch()
  const actionsBoxRef = useRef(null)
  const isCreateBlockActive = useManagementHook(isCreateBlockDialogActive)
  const currentRef = externalActionsRef ? externalActionsRef : actionsBoxRef
  const { isMobile } = useDeviceModeContext()
  const [showMenu, setShowMenu] = useState(false)
  const { isSomethingDragging } = useDragLayer(monitor => ({
    isSomethingDragging: monitor.isDragging(),
  }))
  const highlightedId = useHighlightedEntityId()
  const isResizing = useIsRowResizing()
  const descendantIds = usePage(page =>
    pageSelectors.getAllDescendantIds(page, entity.id),
  )
  const isEditing = useManagement(state =>
    managementSelectors.isEditing(state, entity.id),
  )
  const pageType = usePage(pageSelectors.getPageType)
  const isWebsiteLoginPage = pageType === PageTypeEnum.WebsiteLogin
  const isHighlighted = highlightedId === entity.id
  const [, drag] = useDrag({
    item: {
      entity,
      type: entity.type,
      descendantIds,
      allowedEntityIds: dropBoxAllowedEntityIds,
    },
    canDrag: () => {
      return (
        !isEditing &&
        !isResizing &&
        !isAllowedWebsiteHeaderEntity &&
        isMoveAllowed
      )
    },
  })
  const { showSidebar } = useSidebarVisibilityActionsContext()
  const showCreateBlockDialogAction = useActions(showCreateBlockDialog)
  const copy = useActions(entityActions.copy)
  const remove = useActions(entityActions.remove)
  const moveDown = useActions(entityActions.moveDown)
  const moveUp = useActions(entityActions.moveUp)
  const deviceWarning = utils.getDeviceWarning(
    'appearance' in entity ? entity.appearance : undefined,
    isMobile,
  ) as 'desktop_only' | 'mobile_only'
  const isAllowedWebsiteHeaderEntity = useIsAllowedWebsiteHeaderEntity(
    'parentId' in entity ? entity.parentId : undefined,
    entity.type,
  )

  function handleToggleSettings(e: React.BaseSyntheticEvent) {
    e.stopPropagation()
    const bubblingPath = getBubblingPath(e)
    if (
      !bubblingPath.some(({ id }) =>
        [NO_FOLLOW_CHECKBOX_ID, OPEN_IN_NEW_TAB_CHECKBOX_ID].includes(id),
      ) &&
      e.target.nodeName !== 'INPUT'
    ) {
      e.preventDefault()
    }

    if (!isResizing && utils.notContainActionButtons(e, currentRef)) {
      dispatch(setEditingEntity(entity.id))
      dispatch(setActiveSidebarTab(null))
      showSidebar()
    }
  }

  function hover(e: React.SyntheticEvent) {
    e.stopPropagation()
    if (isEditing) {
      return
    }
    setShowMenu(true)
  }

  function unHover(e: React.SyntheticEvent) {
    e.stopPropagation()
    if (isResizing) {
      return
    }

    if (utils.relatedTargetNotContainsActionButtons(e, currentRef)) {
      setShowMenu(false)
    }
  }

  function getStateColor() {
    if (isHighlighted || isEditing) {
      return highlightedColor
    }
    if (isCreateBlockActive) {
      return null
    }
    if (showMenu) {
      return hoverColor
    }

    if (deviceWarning) {
      return '#EB5447'
    }

    return null
  }

  function handleMoveDown(e: React.SyntheticEvent) {
    e.stopPropagation()
    moveDown(entity)
  }

  function handleMoveUp(e: React.SyntheticEvent) {
    e.stopPropagation()
    moveUp(entity)
  }

  if (
    ('isReadOnly' in entity && entity.isReadOnly) ||
    (isWebsiteLoginPage && entity.type !== EntityTypeEnum.WebsiteLogin)
  ) {
    return (
      <ReadOnlyEntity
        margin={(entity as EntityInterface).margin}
        mobileMargin={(entity as EntityInterface).mobileMargin}
        showInMobileMode={
          (entity as EntityInterface).appearance?.mobile ?? appearance?.mobile
        }
        showInDesktopMode={
          (entity as EntityInterface).appearance?.desktop ?? appearance?.desktop
        }
        flex={flex}
        flexColumn={flexColumn}
      >
        {children}
      </ReadOnlyEntity>
    )
  }
  return (
    <BaseEntityNewUi
      id={entity.id}
      ref={drag}
      stateColor={getStateColor()}
      onClick={handleToggleSettings}
      isEditing={isEditing}
      isSomethingDragging={isSomethingDragging}
      onMouseOver={hover}
      onMouseOut={unHover}
      margin={'margin' in entity ? entity.margin : undefined}
      mobileMargin={'mobileMargin' in entity ? entity.mobileMargin : undefined}
      appearance={'appearance' in entity ? entity.appearance : appearance}
      flex={flex}
      flexColumn={flexColumn}
    >
      {showMenu && (
        <ActionsBox
          ref={currentRef}
          type={entity.type}
          copy={
            'isMasterBlockRoot' in entity && entity.isMasterBlockRoot
              ? false
              : () => copy(entity)
          }
          toggleSettings={handleToggleSettings}
          toggleSaveDialog={() => showCreateBlockDialogAction(entity.id)}
          remove={() => remove(entity)}
          backgroundColor={hoverColor}
          isMasterBlock={
            'masterBlockId' in entity && Boolean(entity.masterBlockId)
          }
          isMoveDownAllowed={
            isAllowedWebsiteHeaderEntity ? false : isMoveDownAllowed
          }
          isMoveUpAllowed={
            isAllowedWebsiteHeaderEntity ? false : isMoveUpAllowed
          }
          isRemoveAllowed={
            isAllowedWebsiteHeaderEntity ? false : isRemoveAllowed
          }
          isCopyAllowed={isAllowedWebsiteHeaderEntity ? false : isCopyAllowed}
          isSaveAllowed={isAllowedWebsiteHeaderEntity ? false : isSaveAllowed}
          moveDown={handleMoveDown}
          moveUp={handleMoveUp}
        />
      )}
      {deviceWarning && <DeviceWarning deviceWarning={deviceWarning} />}
      {children}
    </BaseEntityNewUi>
  )
}

export default BaseEntityNew
